import { Configuration, StoresApi } from '@/api';
import { fetchAllPaginated } from '@/api/utils';
import SelectBox, {
  Item,
  MultiSelectProps,
  OtherSelectBoxProps,
  SingleSelectProps,
} from '@/components/select/SelectBox';
import { Store, StoreAssociation } from '@/models/Store';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { castArrayNonEmpty } from '@/utils/array';

type StoreSelectBoxProps<T extends Item> =
  | (SingleSelectProps<T> &
      OtherSelectBoxProps<T> & {
        multiple?: false;
        retailerId?: string | string[];
        showOrganization?: true;
      })
  | (MultiSelectProps<T> &
      OtherSelectBoxProps<T> & {
        multiple: true;
        retailerId?: string | string[];
        showOrganization?: true;
      });

const formatStoreName = (
  store: { name: string; retailer?: { organization?: { name: string } } },
  showOrganization?: boolean,
) => {
  return showOrganization && store.retailer?.organization
    ? `${store.name} — Organization: ${store.retailer.organization.name}`
    : store.name;
};

const StoreSelectBox = ({ retailerId, showOrganization, ...props }: StoreSelectBoxProps<Store>) => {
  const storesApi = new StoresApi(new Configuration(useAuth));

  const listStores = async (): Promise<Store[]> => {
    const stores = await fetchAllPaginated<Store>(
      (offset, limit) =>
        storesApi.findStores(
          undefined,
          { retailerId },
          offset,
          limit,
          showOrganization ? [StoreAssociation.RETAILER, StoreAssociation.ORGANIZATION] : undefined,
        ),
      100,
      1000,
    );
    return stores.map((store) => ({
      ...store,
      name: formatStoreName(store, showOrganization),
    }));
  };

  const { label = 'Store:', placeholder = 'Select store' } = props;

  const retailerIds = castArrayNonEmpty(retailerId);

  return (
    <SelectBox<Store>
      {...props}
      name="store"
      label={label}
      placeholder={placeholder}
      queryFn={listStores}
      queryKey={['store', showOrganization ? 'withOrganization' : '', ...retailerIds]}
    />
  );
};

export default StoreSelectBox;
